import Phaser from 'phaser';
import type { ResultSceneData } from './scene-data';
import { puzzleNumber } from '@/lib/launch';
import { gameBus } from '@/bridge/gameBus';
import { uiCommandBus, type UiCommand } from '@/bridge/uiCommandBus';
import { dailyArchetype } from '@/lib/keeper';
import { submitResult } from '@/stats/submit-result';
import { getOrCreateClientId } from '@/lib/client-id';
import type { Zone } from '@/lib/zones';
import { isComplete, type LocalStateStore } from '@/lib/local-state';
import { resolveGameConfig } from '@/lib/game-mode';
import { archetypeTier } from '@/lib/archetypes';

export class ResultScene extends Phaser.Scene {
  private resultData!: ResultSceneData;
  private store!: LocalStateStore;
  private unsubscribeCommands?: () => void;
  // Audit follow-up: `dailyArchetype()` is async (SHA-256), so its `.then(…)`
  // continuations can run after this scene is shut down. `isShutDown` lets every
  // continuation bail before touching `this.scene`/`gameBus` (a dead-closure
  // emit would flip currentScene back to RESULT over a live PlayScene).
  private isShutDown = false;
  // Synchronous re-entrancy guard: a practice:start double-tap would otherwise
  // queue two `scene.start('PlayScene')` and clobber the first practice round.
  private practiceStarting = false;

  constructor() {
    super({ key: 'ResultScene' });
  }

  init(data: ResultSceneData): void {
    this.resultData = data;
    // Phaser reuses the scene instance across entries — reset per-entry flags.
    this.isShutDown = false;
    this.practiceStarting = false;
  }

  create(): void {
    // ResultScene is now visually empty — DOM chrome (ResultSummary, ArchetypeCallout,
    // share button, CountdownCard) renders everything. Kept as a Phaser scene because:
    //   1. PlayScene → ResultScene transition is the canonical "shots complete" moment.
    //   2. bfcache pageshow re-emit hooks (Task 31) live on the active scene.
    //   3. scene-routing.ts decides ResultScene from local-state; that contract is unchanged.
    this.store = this.registry.get('store') as LocalStateStore;
    this.resubscribeToCommands();

    void dailyArchetype(this.resultData.date).then((archetype) => {
      if (this.isShutDown) return; // continuation outlived shutdown — drop it
      gameBus.emit({
        type: 'scene:enter',
        payload: {
          scene: 'RESULT',
          date: this.resultData.date,
          puzzleNumber: puzzleNumber(this.resultData.date),
          archetype,
          record: this.resultData.record,
          streak: this.resultData.streak,
        },
      });
    });

    this.maybeSubmitDaily();

    const handler = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      // §3.4 (codex): bfcache restore does NOT re-run create(), so re-register
      // the reverse command channel here or practice taps go dead after restore.
      this.resubscribeToCommands();
      void dailyArchetype(this.resultData.date).then((archetype) => {
        if (this.isShutDown) return; // dead-closure guard: a Practice-Again tap
        // during this bfcache restore's in-flight digest could shut us down; a
        // stale scene:enter RESULT would then flip currentScene over a live PlayScene.
        gameBus.emit({
          type: 'scene:enter',
          payload: {
            scene: 'RESULT',
            date: this.resultData.date,
            puzzleNumber: puzzleNumber(this.resultData.date),
            archetype,
            record: this.resultData.record,
            streak: this.resultData.streak,
          },
        });
      });
    };
    window.addEventListener('pageshow', handler as EventListener);
    this.events.once('shutdown', () => {
      this.isShutDown = true;
      window.removeEventListener('pageshow', handler as EventListener);
      this.unsubscribeCommands?.();
    });
  }

  /**
   * §3.4 (codex): subscribe to the reverse command channel. Called from BOTH
   * create() and the pageshow handler — bfcache restore does NOT re-run
   * create(), so without the pageshow call the practice taps go dead.
   * Unsubscribes any prior subscription first so a pageshow re-subscribe never
   * double-fires.
   */
  private resubscribeToCommands(): void {
    this.unsubscribeCommands?.();
    this.unsubscribeCommands = uiCommandBus.subscribe((c: UiCommand) => this.handleCommand(c));
  }

  /**
   * §4.2 (codex): submit guard. POST only the AUTHORITATIVE store daily record
   * for this date — never a practice/ephemeral record. The writesRecord=false
   * gate in PlayScene.runShot is necessary but not sufficient (practice exits
   * via returnTo, which carries the real daily record), so this is the second
   * line of defense. Named maybeSubmitDaily so it does not collide with the
   * imported submitResult() it calls.
   *
   * Plan #5 §6.3: submit when the record is complete for ITS schema —
   * 5-zone payload for v1, 7-zone for v2. The worker validator (Task 14)
   * accepts both shapes; shot6_zone/shot7_zone are optional in
   * ResultSubmissionPayload. An in-module session Set (inside
   * submit-result.ts) prevents same-context re-POST; full reload re-POSTs
   * and D1's ON CONFLICT(date, client_id) DO NOTHING makes it idempotent.
   * Stored zones are PLAYER-AIM (targetZone), matching v1 column semantics
   * in `daily_stats.zone_votes_shotN`.
   */
  private maybeSubmitDaily(): void {
    const record = this.resultData.record;
    const authoritative = this.store.getDailyRecord(this.resultData.date);
    if (authoritative !== record) return; // ephemeral/practice record — never POST
    if (!isComplete(record)) return;
    const basePayload = {
      date: this.resultData.date,
      client_id: getOrCreateClientId(),
      score: record.score,
      shot1_zone: record.shots[0]!.targetZone as Zone,
      shot2_zone: record.shots[1]!.targetZone as Zone,
      shot3_zone: record.shots[2]!.targetZone as Zone,
      shot4_zone: record.shots[3]!.targetZone as Zone,
      shot5_zone: record.shots[4]!.targetZone as Zone,
    };
    void submitResult(record.schemaVersion === 2
      ? { ...basePayload, shot6_zone: record.shots[5]!.targetZone as Zone, shot7_zone: record.shots[6]!.targetZone as Zone }
      : basePayload);
  }

  private handleCommand(c: UiCommand): void {
    // Only practice:start. practice:exit is intentionally NOT handled here —
    // PlayScene.exitPractice owns it (the scene actually active during practice;
    // ResultScene is shut down by the time the banner can fire). Handling it here
    // would be a latent transition-window double-handler (audit follow-up).
    if (c.type !== 'practice:start') return;
    if (this.practiceStarting) return; // synchronous double-tap guard
    this.practiceStarting = true;
    void dailyArchetype(this.resultData.date)
      .then((today) => {
        if (this.isShutDown) return; // continuation outlived shutdown — drop it
        const tier = archetypeTier(today);
        this.scene.start('PlayScene', {
          date: this.resultData.date,
          archetype: today,
          existingShots: [],
          config: resolveGameConfig({ mode: 'practice', tier, assistEnabled: false }),
          returnTo: {
            date: this.resultData.date,
            record: this.resultData.record,
            streak: this.resultData.streak,
          },
        });
      })
      // Release the guard if the digest rejects, so the button isn't stuck.
      .catch(() => { this.practiceStarting = false; });
  }
}
